import { Info, Sparkles } from 'lucide-react'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip'

/**
 * Pairs a setting label with an info icon. Hover (or tap on mobile) reveals
 * a longer "what / why / when to change" explanation. Caller wraps in
 * <TooltipProvider> at section level.
 */
export function SettingLabel({
  label,
  what,
  why,
  whenToChange,
  className,
}: {
  label: string
  what: string
  why?: string
  whenToChange?: { symptom: string; action: string }[]
  className?: string
}) {
  return (
    <div className={`flex items-center gap-1.5 ${className || ''}`}>
      <Label className="text-xs">{label}</Label>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label={`Help: ${label}`}
          >
            <Info className="h-3 w-3" />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="max-w-sm text-left whitespace-normal bg-popover text-popover-foreground border shadow-lg p-3 space-y-2"
        >
          <p className="text-xs leading-relaxed">{what}</p>
          {why && (
            <p className="text-[11px] leading-relaxed text-muted-foreground">
              <span className="font-semibold">Why it matters:</span> {why}
            </p>
          )}
          {whenToChange && whenToChange.length > 0 && (
            <div className="text-[11px] space-y-0.5 pt-1 border-t">
              <p className="font-semibold">When to change:</p>
              {whenToChange.map((rule, i) => (
                <p key={i} className="leading-relaxed text-muted-foreground">
                  <span className="text-foreground/80">{rule.symptom}</span> → {rule.action}
                </p>
              ))}
            </div>
          )}
        </TooltipContent>
      </Tooltip>
    </div>
  )
}

/**
 * Production-grade presets — replace the old Max Speed / Balanced / Max
 * Quality which had inconsistent stacks and weren't tuned for real-world
 * use cases. Each preset sets the FULL stack (LLM, TTS model, ASR + lang,
 * voice tuning, idle, interruption) so applying it produces a known-good
 * config end-to-end. voiceId is intentionally NOT included — applying a
 * preset preserves the admin's voice character choice.
 *
 * Verified 2026-04-27 against direct Telnyx API + real call transcripts.
 */
export const PRESETS = {
  english_pro: {
    label: 'English Pro',
    description: 'Production-tested English-only stack — Molly + GPT-4o + Flash v2 + tuned interruption gate (0.9s wait). For firms running real English traffic.',
    config: {
      // Production-tested values from real-call A/B (Settle My Claim,
      // 2026-04-30). gpt-4o > claude-haiku-4-5 for intake quality.
      // 2026-05-05: interruption wait bumped 0.6→0.9 after affiliate demo
      // reported the inbound greeting being cut to "Good morning, thank y..."
      // by connect-time line static. 0.9s still feels snappier than the
      // 1.5s static default while leaving enough headroom for handset noise.
      voiceId: 'molly',                     // ElevenLabs warm professional female
      llmModel: 'openai/gpt-4o',            // beat claude-haiku-4-5 in production A/B
      ttsModel: 'eleven_flash_v2',          // English-only, ~75ms latency
      transcriptionModel: 'deepgram/flux',  // English-only ASR, ~50ms
      transcriptionLanguage: 'en',
      stability: 0.7,                       // → Telnyx voice_settings.temperature
      similarityBoost: 0.8,                 // → Telnyx voice_settings.similarity_boost
      voiceSpeed: 1.1,                      // slightly snappy receptionist pace
      voiceStyle: 0.2,                      // warm receptionist (raised from 0.15)
      userIdleReplySeconds: 15, userIdleTimeoutSeconds: 90, timeLimitSeconds: 1800,
      interruptionEnabled: true,
      interruptionWaitSeconds: 0.9,         // bumped from 0.6 — see comment above
      interruptionOnPunctuationSeconds: 0.45,
      interruptionOnNoPunctuationSeconds: 1.5,
      crossCallMemoryEnabled: true,
      customLlmProviderId: null as string | null,
      bundledLlmApiKeyRef: null as string | null,
      // OMITTED on purpose: audioFormat (pcm_8000), turnEagerness (eager),
      // speculativeTurn (true), optimizeStreamingLatency (3),
      // backgroundVoiceDetection (true) — these were the production-tested
      // values from the original ElevenLabs Conversational AI path. The
      // Joi schema still accepts them for back-compat but the entire EL
      // Conv-AI integration was deleted ~2026-04-24 when we moved to
      // Telnyx-only voice. They live in elevenLabsDefaults.ts only and
      // never reach Telnyx today — including them in the preset would
      // mislead admins into thinking they affect calls.
    },
  },
  multilingual_pro: {
    label: 'ES/EN Bilingual',
    description: 'Code-switching English ↔ Spanish in one voice. Rime ArcanaV3 Luna + Whisper Turbo + GPT-4o. For callers who mix languages mid-call.',
    config: {
      // Rime ArcanaV3 is purpose-built for mid-utterance language switching
      // across 10 languages (verified 2026-04-27 via direct Telnyx probe). A
      // single Rime voice handles ES + EN cleanly without re-loading. The
      // earlier preset left voiceId unset, which meant clicking "Multilingual
      // Pro" on a Molly/Lauren ElevenLabs voice still routed Spanish through
      // ElevenLabs's English-flavored Spanish — not the result the label
      // implied. Setting rime_luna here closes that gap.
      voiceId: 'rime_luna',
      // ElevenLabs-only fields (ttsModel, stability, similarityBoost,
      // voiceStyle) are intentionally omitted — buildVoiceSettings drops
      // them for non-ElevenLabs voices anyway, so leaving them out keeps
      // the form honest when this preset is applied.
      llmModel: 'openai/gpt-4o',                            // production-tested better than Haiku for ES/EN intake
      transcriptionModel: 'openai/whisper-large-v3-turbo',  // Telnyx-recommended for code-switching
      transcriptionLanguage: 'auto',
      voiceSpeed: 1.0,
      userIdleReplySeconds: 15, userIdleTimeoutSeconds: 90, timeLimitSeconds: 1800,
      // Interruption OFF for multilingual: Spanish pause-fillers ("este…",
      // "pues…") trip the interruption gate and yield turn prematurely.
      interruptionEnabled: false,
      interruptionWaitSeconds: 1.5,
      interruptionOnPunctuationSeconds: 0.1,
      interruptionOnNoPunctuationSeconds: 1.5,
      crossCallMemoryEnabled: true,
      customLlmProviderId: null as string | null,
      bundledLlmApiKeyRef: null as string | null,
    },
  },
  spanish_native: {
    label: 'Spanish Native',
    description: 'Native Spanish quality — Telnyx NaturalHD Eris (es-MX female) + GPT-4o + Whisper Turbo. Voices trained on Spanish speakers, no English-accent leak. For Spanish-first firms.',
    config: {
      // Telnyx NaturalHD has 13 dedicated Spanish voices trained on actual
      // Spanish speakers — no "English-flavored Spanish" issue ElevenLabs
      // exhibits. eris is the warmest es-MX female; for Spain-Spanish swap
      // to lark (es-ES), for US heritage swap to pola (es-US Dominican).
      // Browse the Telnyx library modal to A/B against male voices like
      // celestino (es-MX) or aurelio (es-US Heritage).
      voiceId: 'Telnyx.NaturalHD.eris',
      llmModel: 'openai/gpt-4o',                            // matches ES/EN Bilingual — beat Haiku in ES intake A/B
      transcriptionModel: 'openai/whisper-large-v3-turbo',
      // 'auto' rather than 'es' so English filler words ("OK", "thanks",
      // "yeah") still transcribe correctly when a Spanish caller code-mixes
      // briefly. Locking to 'es' is faster but trips on English fillers.
      transcriptionLanguage: 'auto',
      voiceSpeed: 1.0,
      userIdleReplySeconds: 15, userIdleTimeoutSeconds: 90, timeLimitSeconds: 1800,
      interruptionEnabled: false,
      interruptionWaitSeconds: 1.5,
      interruptionOnPunctuationSeconds: 0.1,
      interruptionOnNoPunctuationSeconds: 1.5,
      crossCallMemoryEnabled: true,
      customLlmProviderId: null as string | null,
      bundledLlmApiKeyRef: null as string | null,
    },
  },
  premium_quality: {
    label: 'Premium Quality',
    description: 'Highest-quality stack regardless of latency. Newest flagship LLM + premium voice tuning. For high-stakes calls.',
    config: {
      ttsModel: 'eleven_turbo_v2_5',                        // higher quality multilingual
      llmModel: 'openai/gpt-5.2',                           // newest OpenAI flagship
      transcriptionModel: 'openai/whisper-large-v3-turbo',  // best multilingual accuracy
      transcriptionLanguage: 'auto',
      stability: 0.6, similarityBoost: 0.7, voiceSpeed: 0.95, voiceStyle: 0.2,
      userIdleReplySeconds: 20, userIdleTimeoutSeconds: 120, timeLimitSeconds: 1800,
      interruptionEnabled: true,
      interruptionWaitSeconds: 1.2,
      interruptionOnPunctuationSeconds: 0.1,
      interruptionOnNoPunctuationSeconds: 1.5,
      crossCallMemoryEnabled: true,
      customLlmProviderId: null as string | null,
      bundledLlmApiKeyRef: null as string | null,
    },
  },
  british_pro: {
    label: 'British Pro',
    description: 'Charlotte (calm British female) on the Multilingual-Pro stack. For firms with a UK brand identity or callers who prefer a British accent.',
    config: {
      voiceId: 'charlotte',                                  // ElevenLabs Charlotte — verified British female
      ttsModel: 'eleven_flash_v2_5',                         // 32 languages — handles UK callers + occasional non-EN
      llmModel: 'anthropic/claude-haiku-4-5',                // bundled, multilingual, no loop bugs
      transcriptionModel: 'openai/whisper-large-v3-turbo',   // best on accents (Whisper > Flux for UK English)
      transcriptionLanguage: 'auto',
      stability: 0.5, similarityBoost: 0.55, voiceSpeed: 1.0, voiceStyle: 0.15,
      userIdleReplySeconds: 15, userIdleTimeoutSeconds: 90, timeLimitSeconds: 1800,
      interruptionEnabled: true,
      interruptionWaitSeconds: 1.0,
      interruptionOnPunctuationSeconds: 0.1,
      interruptionOnNoPunctuationSeconds: 1.5,
      crossCallMemoryEnabled: true,
      customLlmProviderId: null as string | null,
      bundledLlmApiKeyRef: null as string | null,
    },
  },
} as const

export type PresetId = keyof typeof PRESETS

/**
 * The recommended baseline values for a law firm receptionist — used by
 * the "Apply baseline" callout. Mirrors the ES/EN Bilingual preset since
 * legal-intake callers are commonly bilingual and we'd rather start admins
 * on a stack that handles both than one that breaks on first Spanish call.
 * The preset already picks rime_luna as the voice (purpose-built for
 * code-switching) — overriding to molly would re-introduce the
 * English-flavored-Spanish issue this baseline is supposed to avoid.
 */
export const LAW_FIRM_BASELINE = {
  ...PRESETS.multilingual_pro.config,
} as const

/**
 * Top-of-section callout that surfaces the recommended baseline + a one-
 * click apply. Doesn't auto-save — user clicks Apply, then Save & Sync.
 */
export function RecommendedBaseline({ onApply, onPreview }: { onApply: () => void; onPreview?: () => void }) {
  return (
    <div className="rounded-md border border-primary/30 bg-primary/5 p-3 space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            <Sparkles className="h-3.5 w-3.5 text-primary" />
            <span className="text-xs font-semibold">Recommended baseline — Bilingual law firm receptionist</span>
          </div>
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            ES/EN Bilingual stack (Rime ArcanaV3 Luna + Whisper Turbo + GPT-4o) tuned for bilingual legal intake.
            Single voice handles English ↔ Spanish mid-call code-switching cleanly. Click Apply, then Save &amp; Sync to push to Telnyx.
          </p>
          <p className="text-[10px] text-muted-foreground font-mono leading-relaxed">
            Rime Luna · Whisper Turbo (auto) · GPT-4o · interruption OFF · reply 15s · memory ON
          </p>
        </div>
        <div className="flex flex-col gap-1.5 shrink-0">
          <Button type="button" size="sm" onClick={onApply} className="h-7 text-xs">
            Apply baseline
          </Button>
          {onPreview && (
            <Button type="button" size="sm" variant="outline" onClick={onPreview} className="h-7 text-xs">
              Preview diff
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Wraps children in a single TooltipProvider scoped to the Voice Engine section.
 * Saves us from importing TooltipProvider at every callsite.
 */
export function VoiceEngineTooltipScope({ children }: { children: React.ReactNode }) {
  return <TooltipProvider delayDuration={200}>{children}</TooltipProvider>
}
